import { useEffect, useRef, useState, type ReactNode } from "react";
import { CheckCircle2, Copy, Loader2, XCircle } from "lucide-react";
import { type CarouselComposeLogEntry, type CarouselItem, type CarouselLlmEvent } from "@/lib/api";
import { copyText, packFromItem, type ConceptsTraceOutgoing } from "@/lib/carouselGenerationPack";
import { CTA_IO, type CtaIoKey } from "@/lib/carouselStudioMeta";
import { platformEnding } from "@/lib/carouselCaptionCompose";
import { estimateRenderProgress, etaLabel } from "@/lib/renderProgress";
import LlmJobDebug from "@/components/LlmJobDebug";
import LlmReceiveProgress from "@/components/LlmReceiveProgress";
import PublishJobProgress from "@/components/PublishJobProgress";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { StudioModalBody, StudioModalFooter, StudioModalFrame, StudioModalHeader } from "@/components/StudioModalFrame";
import {
  DesignProposalList,
  collectProposalSelected,
  defaultProposalPicked,
  reviewProposals,
} from "@/components/DesignReviewPanel";
import { cn } from "@/lib/utils";
import { platformPostHref } from "@/lib/publishPlatformStatus";

export type AiJobKind =
  | "concepts"
  | "inspiration"
  | "fromDraft"
  | "caption"
  | "layoutPlan"
  | "copy"
  | "copyRevise"
  | "render"
  | "layoutQa"
  | "designReview"
  | "designApply"
  | "houseFix"
  | "retro"
  | "publish"
  | "post"
  | "reel";

export const AI_JOB_TITLE: Record<AiJobKind, string> = {
  concepts: "4 koncepcje",
  inspiration: "Analiza inspiracji",
  fromDraft: "Szkic na slajdy",
  caption: "Opis pod postem",
  layoutPlan: "Plan układu",
  copy: "Generator treści",
  copyRevise: "Poprawka copy",
  render: "Render obrazków",
  layoutQa: "QA składu",
  designReview: "Ocena designu",
  designApply: "Zastosowanie designu",
  houseFix: "Poprawa serii",
  retro: "Retro",
  publish: "Publikacja",
  post: "Post 1-grafika",
  reel: "Rolka na FB",
};

const JOB_IO: Record<AiJobKind, CtaIoKey> = {
  concepts: "concepts",
  inspiration: "inspiration",
  fromDraft: "fromDraft",
  caption: "caption",
  layoutPlan: "layoutPlan",
  copy: "copy",
  copyRevise: "copyRevise",
  render: "render",
  layoutQa: "layoutQa",
  designReview: "designReview",
  designApply: "designApply",
  houseFix: "houseFix",
  retro: "retro",
  publish: "publish",
  post: "post",
  reel: "reel",
};

export type AiJobParam = { label: string; value: string };

export default function AiJobModal({
  open,
  kind,
  item,
  params,
  outgoing,
  running,
  failed,
  statusLabel,
  error,
  abortPending,
  onAbort,
  onClose,
  onApplyVerdict,
  applyPending,
  output,
}: {
  open: boolean;
  kind: AiJobKind | null;
  item?: CarouselItem;
  params: AiJobParam[];
  outgoing?: ConceptsTraceOutgoing | null;
  running: boolean;
  failed: boolean;
  statusLabel: string;
  error?: string;
  abortPending?: boolean;
  onAbort?: () => void;
  onClose: () => void;
  onApplyVerdict?: (selected: number[]) => void;
  applyPending?: boolean;
  output?: ReactNode;
}) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !running) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, running, onClose]);

  if (!open || !kind) return null;

  const io = CTA_IO[JOB_IO[kind]];
  const title = AI_JOB_TITLE[kind];
  const canCopy = kind === "concepts" || kind === "post";

  const copyPack = async () => {
    const ok = await copyText(packFromItem(item, outgoing));
    setCopied(ok);
    window.setTimeout(() => setCopied(false), 2500);
  };

  return (
    <StudioModalFrame labelledBy="ai-job-title">
      <StudioModalHeader>
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 id="ai-job-title" className="text-base font-medium">
              {title}
            </h2>
            <p className="mt-1 flex items-center gap-2 text-sm text-muted-foreground" aria-live="polite">
              {running ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin text-brand" />
              ) : failed ? (
                <XCircle className="h-3.5 w-3.5 text-destructive" />
              ) : (
                <CheckCircle2 className="h-3.5 w-3.5 text-success" />
              )}
              {statusLabel}
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              {io.take} {io.give}
            </p>
          </div>
          <div className="flex gap-2">
            {running && onAbort ? (
              <Button size="sm" variant="outline" disabled={abortPending} onClick={onAbort}>
                Przerwij
              </Button>
            ) : null}
            <Button size="sm" variant="outline" disabled={running} onClick={onClose}>
              Zamknij
            </Button>
          </div>
        </div>
      </StudioModalHeader>

      <StudioModalBody className="flex flex-col overflow-hidden p-0">
        <div className="grid min-h-0 flex-1 gap-0 overflow-auto lg:grid-cols-4 lg:overflow-hidden">
          <section className="border-b border-border p-5 lg:overflow-auto lg:border-b-0 lg:border-r">
            <h3 className="mb-3 text-sm font-medium">Wejście</h3>
            {params.length ? (
              <dl className="grid grid-cols-2 gap-x-3 gap-y-2 text-xs">
                {params.map((row) => (
                  <div key={row.label}>
                    <dt className="text-muted-foreground">{row.label}</dt>
                    <dd className="font-medium text-foreground">{row.value || "brak"}</dd>
                  </div>
                ))}
              </dl>
            ) : (
              <p className="text-xs text-muted-foreground">Brak parametrów tego strzału.</p>
            )}
          </section>

          <section className="border-b border-border p-5 lg:overflow-auto lg:border-b-0 lg:border-r">
            <h3 className="mb-3 text-sm font-medium">Przetwarzanie</h3>
            {kind === "render" ? (
              <RenderJobProgress item={item} running={running} error={error} />
            ) : kind === "reel" ? (
              <ReelJobProgress item={item} running={running} error={error} />
            ) : kind === "publish" ? (
              <PublishJobProgress item={item} running={running} error={error} />
            ) : (
              <>
                <LlmReceiveProgress progress={item?.llm_progress} llmStatus={item?.llm_status} />
                <LlmJobDebug className="mt-3" item={item} progress={item?.llm_progress} />
                {error ? <p className="mt-3 text-sm text-destructive">{error}</p> : null}
                {!running && !error && !item?.llm_progress?.phase ? (
                  <p className="text-xs text-muted-foreground">Jeszcze nic. Status pojawi się po starcie joba.</p>
                ) : null}
              </>
            )}
          </section>

          <section className="border-b border-border p-5 lg:overflow-auto lg:border-b-0 lg:border-r">
            <h3 className="mb-3 text-sm font-medium">Wyjście</h3>
            {output || (
              <DefaultJobOutput
                kind={kind}
                item={item}
                running={running}
                applyPending={applyPending}
                onApplyVerdict={onApplyVerdict}
              />
            )}
          </section>

          <JobLogColumn item={item} running={running} />
        </div>
      </StudioModalBody>

      <StudioModalFooter className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-muted-foreground">
          Okno nie zamyka się samo. Klik w ten sam przycisk AI otwiera je ponownie, nie startuje drugiego strzału.
        </p>
        {canCopy ? (
          <Button size="sm" variant="outline" disabled={running} onClick={copyPack}>
            <Copy className="mr-2 h-3.5 w-3.5" />
            {copied ? "Skopiowano" : "Kopiuj przebieg"}
          </Button>
        ) : null}
      </StudioModalFooter>
    </StudioModalFrame>
  );
}

const LOG_KIND_LABEL: Record<string, string> = {
  start: "start",
  score_now: "score teraz",
  score_after: "score po",
  prompt_analyze: "prompt analizy",
  prompt_verify: "prompt weryfikacji",
  response: "odpowiedź",
  ops: "operacje",
  verify: "weryfikacja",
  stop: "stop",
  call: "wysyłam",
  first_token: "leci tekst",
  alive: "czekam na znak",
  prompt_ready: "prompt gotowy",
};

type JobLogRow = {
  at: string;
  kind: string;
  turn?: number;
  text?: string;
  prompt?: string;
  raw?: string;
  ops?: string[];
  model?: string;
};

function clockOf(at?: string): string {
  if (!at) return "";
  const hit = at.match(/T(\d{2}:\d{2}:\d{2})/);
  return hit?.[1] || at.slice(11, 19);
}

function collectJobLog(item?: CarouselItem): JobLogRow[] {
  const rows: JobLogRow[] = [];
  for (const entry of item?.design_review?.loop?.log || []) {
    rows.push(fromLoopEntry(entry));
  }
  for (const event of item?.llm_progress?.events || []) {
    rows.push(fromLlmEvent(event));
  }
  rows.sort((a, b) => (a.at || "").localeCompare(b.at || ""));
  return rows;
}

function fromLoopEntry(entry: CarouselComposeLogEntry): JobLogRow {
  return {
    at: entry.at || "",
    kind: entry.kind || "log",
    turn: entry.turn,
    text: entry.text,
    prompt: entry.prompt,
    raw: entry.raw,
    ops: entry.ops,
    model: entry.model,
  };
}

function fromLlmEvent(event: CarouselLlmEvent): JobLogRow {
  const bits = [event.detail, event.chars != null ? `${event.chars} zn` : ""].filter(Boolean);
  return {
    at: event.at || "",
    kind: event.event || "event",
    text: bits.join(" · "),
    model: event.model,
  };
}

function formatLogLine(row: JobLogRow): string {
  const when = clockOf(row.at);
  const kind = LOG_KIND_LABEL[row.kind] || row.kind;
  const turn = row.turn ? `tura ${row.turn}` : "";
  const head = [when, turn, kind].filter(Boolean).join("  ");
  const chunks = [head];
  if (row.model) chunks.push(row.model);
  if (row.ops?.length) chunks.push(row.ops.join(", "));
  if (row.text) chunks.push(row.text);
  if (row.prompt) chunks.push(`PROMPT\n${row.prompt}`);
  if (row.raw) chunks.push(`ODPOWIEDŹ\n${row.raw}`);
  return chunks.join(" · ");
}

function JobLogColumn({ item, running }: { item?: CarouselItem; running: boolean }) {
  const [copied, setCopied] = useState(false);
  const scroller = useRef<HTMLDivElement | null>(null);
  const rows = collectJobLog(item);
  const detail = (item?.llm_detail || "").trim();

  useEffect(() => {
    const node = scroller.current;
    if (!node) return;
    node.scrollTop = node.scrollHeight;
  }, [rows.length, detail]);

  const copyLog = async () => {
    const lines = rows.map(formatLogLine);
    if (detail && !lines.some((line) => line.includes(detail))) lines.push(detail);
    const ok = await copyText(lines.join("\n\n") || "brak wpisów");
    setCopied(ok);
    window.setTimeout(() => setCopied(false), 2500);
  };

  return (
    <section className="flex min-h-[16rem] flex-col p-5 lg:min-h-0">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h3 className="text-sm font-medium">Log</h3>
        <Button size="sm" variant="outline" onClick={copyLog}>
          <Copy className="mr-2 h-3.5 w-3.5" />
          {copied ? "Skopiowano" : "Kopiuj log"}
        </Button>
      </div>
      <div
        ref={scroller}
        className="min-h-0 flex-1 overflow-auto rounded-md border border-border bg-muted/30 p-2 text-[11px] leading-5"
      >
        {rows.length ? (
          rows.map((row, i) => (
            <article key={`${row.at}-${row.kind}-${i}`} className="border-b border-border/60 py-1.5 last:border-b-0">
              <p className="font-medium text-foreground">
                {clockOf(row.at) ? `${clockOf(row.at)}  ` : ""}
                {row.turn ? `tura ${row.turn} · ` : ""}
                {LOG_KIND_LABEL[row.kind] || row.kind}
                {row.model ? ` · ${row.model}` : ""}
              </p>
              {row.ops?.length ? <p className="text-muted-foreground">{row.ops.join(", ")}</p> : null}
              {row.text ? <p className="whitespace-pre-wrap text-muted-foreground">{row.text}</p> : null}
              {row.prompt ? (
                <details className="mt-1">
                  <summary className="cursor-pointer text-muted-foreground">prompt</summary>
                  <pre className="mt-1 whitespace-pre-wrap break-words text-foreground">{row.prompt}</pre>
                </details>
              ) : null}
              {row.raw ? (
                <details className="mt-1">
                  <summary className="cursor-pointer text-muted-foreground">odpowiedź modelu</summary>
                  <pre className="mt-1 whitespace-pre-wrap break-words text-foreground">{row.raw}</pre>
                </details>
              ) : null}
            </article>
          ))
        ) : (
          <p className="text-muted-foreground">
            {running ? "Czekam na pierwszy wpis pętli albo workera." : "Jeszcze nic. Log pojawi się po starcie joba."}
          </p>
        )}
        {detail && !rows.some((row) => row.text === detail) ? (
          <p className="mt-2 text-muted-foreground">{detail}</p>
        ) : null}
      </div>
    </section>
  );
}

function DesignVerdictOut({
  kind,
  item,
  running,
  applyPending,
  onApplyVerdict,
}: {
  kind: AiJobKind;
  item?: CarouselItem;
  running: boolean;
  applyPending?: boolean;
  onApplyVerdict?: (selected: number[]) => void;
}) {
  const review = item?.design_review;
  const proposals = reviewProposals(review);
  const [picked, setPicked] = useState<Record<number, boolean>>({});

  useEffect(() => {
    setPicked(defaultProposalPicked(proposals));
  }, [review?.status, review?.verdict, proposals.length]);

  if (kind === "houseFix" && running) {
    return (
      <p className="text-xs text-muted-foreground">
        {item?.llm_detail || "Składam kanon: zdania, hasła, oś, marker, strzałki."}
      </p>
    );
  }
  if (!review && running) return <p className="text-xs text-muted-foreground">Role oceniają serię.</p>;
  if (!review) return <p className="text-xs text-muted-foreground">Jeszcze nic.</p>;
  const applied = (review.applied || []).map(String).filter(Boolean);
  const rejected = (review.rejected || []).map(String).filter(Boolean);
  const pending = review.status === "pending" && proposals.length > 0 && Boolean(onApplyVerdict);
  const selected = collectProposalSelected(proposals, picked);
  return (
    <div className="space-y-2 text-xs">
      {review.house_note ? <p>{review.house_note}</p> : null}
      {review.diagnosis ? <p>{review.diagnosis}</p> : null}
      {review.verdict ? <p>Werdykt: {review.verdict}</p> : null}
      {review.loop?.turns?.length ? (
        <ol className="list-decimal space-y-1 pl-4">
          {review.loop.turns.map((turn) => (
            <li key={turn.turn || turn.analyze}>
              Tura {turn.turn}: {turn.analyze || (turn.after_score?.ok ? "zielony" : "fail")}
              {turn.delta?.improved?.length ? ` · lepiej ${turn.delta.improved.join(", ")}` : ""}
            </li>
          ))}
        </ol>
      ) : null}
      {applied.length && review.status === "applied" ? (
        <div>
          <p className="font-medium">Zastosowano</p>
          <ul className="list-disc pl-4">
            {applied.map((label, i) => (
              <li key={`${label}-${i}`}>{label}</li>
            ))}
          </ul>
        </div>
      ) : null}
      {rejected.length && review.status === "applied" ? (
        <div>
          <p className="font-medium">Odrzucone</p>
          <ul className="list-disc pl-4">
            {rejected.map((label, i) => (
              <li key={`${label}-${i}`}>{label}</li>
            ))}
          </ul>
        </div>
      ) : null}
      {pending ? (
        <div className="space-y-2">
          <DesignProposalList proposals={proposals} picked={picked} onPicked={setPicked} interactive />
          <Button
            size="sm"
            variant="outline"
            disabled={applyPending || running || selected.length === 0}
            onClick={() => onApplyVerdict?.(selected)}
          >
            {applyPending ? "Wdrażam zaznaczone…" : "Wdróż zaznaczone"}
          </Button>
        </div>
      ) : proposals.length && review.status !== "applied" ? (
        <DesignProposalList proposals={proposals} />
      ) : null}
    </div>
  );
}

function DefaultJobOutput({
  kind,
  item,
  running,
  applyPending,
  onApplyVerdict,
}: {
  kind: AiJobKind;
  item?: CarouselItem;
  running: boolean;
  applyPending?: boolean;
  onApplyVerdict?: (selected: number[]) => void;
}) {
  if (kind === "concepts" || kind === "post") {
    const ideas = item?.ideas || [];
    return (
      <div className="space-y-2">
        <p className="text-xs text-muted-foreground">{ideas.length} / 4 kart</p>
        {Array.from({ length: 4 }, (_, i) => {
          const idea = ideas[i];
          return (
            <div
              key={idea?.id || `slot-${i}`}
              className={cn(
                "rounded-md border p-2 text-xs",
                idea ? "border-success/40 bg-success/5" : "border-dashed border-border bg-muted/20",
              )}
            >
              {idea ? (
                <>
                  <div className="mb-1 flex items-center gap-2">
                    <Badge variant="outline">{idea.format_id || idea.format_source}</Badge>
                  </div>
                  <div className="font-medium">{idea.hook}</div>
                </>
              ) : (
                <p className="text-muted-foreground">{running ? `Koncepcja ${i + 1} jeszcze nie wróciła.` : "Puste."}</p>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  if (kind === "inspiration") {
    const analysis = item?.inspiration_analysis;
    if (!analysis && running) return <p className="text-xs text-muted-foreground">Czekam na odczyt slajdów.</p>;
    if (!analysis) return <p className="text-xs text-muted-foreground">Jeszcze nic.</p>;
    return (
      <div className="space-y-2 text-xs">
        {analysis.error ? <p className="text-destructive">{analysis.error}</p> : null}
        {analysis.idea ? <p>{analysis.idea}</p> : null}
        {analysis.layout_note ? <p>Układ: {analysis.layout_note}</p> : null}
        {analysis.do_not_copy ? <p className="text-muted-foreground">Nie kopiuj: {analysis.do_not_copy}</p> : null}
      </div>
    );
  }

  if (kind === "layoutPlan") {
    const plan = item?.layout_plan;
    if (!plan && running) return <p className="text-xs text-muted-foreground">Składam plan układu.</p>;
    if (!plan) return <p className="text-xs text-muted-foreground">Jeszcze nic.</p>;
    return (
      <div className="space-y-2 text-xs">
        {plan.rationale ? <p>{plan.rationale}</p> : null}
        <p className="text-muted-foreground">
          {plan.format_id || "typ"}
          {plan.pattern ? ` · ${plan.pattern}` : ""}
        </p>
        {(plan.slides || []).map((row) => (
          <p key={`${row.index}-${row.recipe}`}>
            {row.index}. {row.recipe}
            {row.why ? ` · ${row.why}` : ""}
          </p>
        ))}
      </div>
    );
  }

  if (kind === "copy" || kind === "copyRevise" || kind === "fromDraft") {
    const slides = item?.copy?.slides || [];
    const review = item?.discussion?.copy_review;
    const revision = item?.discussion?.copy_revision;
    const plan = item?.layout_plan;
    if (!slides.length && running && kind === "fromDraft") {
      if (plan?.slides?.length) {
        return (
          <div className="space-y-2 text-xs">
            <p className="text-muted-foreground">Kładę szkic na ten plan.</p>
            {(plan.slides || []).map((row) => (
              <p key={`${row.index}-${row.recipe}`}>
                {row.index}. {row.recipe}
              </p>
            ))}
          </div>
        );
      }
      return <p className="text-xs text-muted-foreground">Najpierw plan układu, potem ściany.</p>;
    }
    if (!slides.length && running) return <p className="text-xs text-muted-foreground">Role i redaktor jeszcze piszą.</p>;
    if (!slides.length) return <p className="text-xs text-muted-foreground">Jeszcze nic.</p>;
    return (
      <div className="space-y-2 text-xs">
        <p>{slides.length} slajdów</p>
        {review?.verdict ? <p>Recenzja: {review.verdict}</p> : null}
        {revision?.summary ? <p>{revision.summary}</p> : null}
      </div>
    );
  }

  if (kind === "caption") {
    const text = item?.caption || "";
    if (!text && running) return <p className="text-xs text-muted-foreground">Piszę opis pod postem.</p>;
    if (!text) return <p className="text-xs text-muted-foreground">Jeszcze nic.</p>;
    const check = item?.discussion?.caption_cta;
    const via = item?.short_url || "";
    return (
      <div className="space-y-2 text-xs">
        <p className="whitespace-pre-wrap">{text}</p>
        <p className="text-muted-foreground whitespace-pre-wrap">FB: {platformEnding("fb", via)}</p>
        <p className="text-muted-foreground whitespace-pre-wrap">IG: {platformEnding("ig")}</p>
        {item?.caption_engage ? <p className="text-muted-foreground">{item.caption_engage}</p> : null}
        {check?.verdict ? (
          <p>
            CTA: {check.verdict}
            {check.note ? ` · ${check.note}` : ""}
          </p>
        ) : running ? (
          <p className="text-muted-foreground">Sprawdzam, czy to jest w książkach.</p>
        ) : null}
        {(check?.hits || []).map((hit, i) => (
          <p key={`${hit.book_id || "hit"}-${i}`} className="text-muted-foreground">
            {hit.label || hit.book_id}: {hit.excerpt}
          </p>
        ))}
      </div>
    );
  }

  if (kind === "layoutQa") {
    const qa = item?.layout_qa;
    if (!qa && running) return <p className="text-xs text-muted-foreground">Sprawdzam skład.</p>;
    if (!qa) return <p className="text-xs text-muted-foreground">Jeszcze nic.</p>;
    return <p className="text-xs">{qa.summary || (qa.ok ? "Skład OK" : "Skład do poprawki")}</p>;
  }

  if (kind === "designReview" || kind === "designApply" || kind === "houseFix") {
    return (
      <DesignVerdictOut
        kind={kind}
        item={item}
        running={running}
        applyPending={applyPending}
        onApplyVerdict={onApplyVerdict}
      />
    );
  }

  if (kind === "retro") {
    if (!item?.retro && running) return <p className="text-xs text-muted-foreground">Role robią retro.</p>;
    if (!item?.retro) return <p className="text-xs text-muted-foreground">Jeszcze nic.</p>;
    return (
      <div className="space-y-2 text-xs">
        {item.retro.decision ? <p>{item.retro.decision}</p> : null}
        <p>{(item.lessons || []).length} wniosków</p>
      </div>
    );
  }

  if (kind === "render") {
    const done = item?.render_done || 0;
    const total = item?.render_total || 0;
    if (!done && running) return <p className="text-xs text-muted-foreground">Pierwszy slajd jeszcze się składa.</p>;
    return <p className="text-xs">{done} / {total || "?"} PNG</p>;
  }

  if (kind === "reel") {
    if (item?.has_reel) {
      return <p className="text-xs">reel.mp4 gotowe{item.reel_track_title ? ` · ${item.reel_track_title}` : ""}</p>;
    }
    return <p className="text-xs text-muted-foreground">{running ? "Składam rolkę." : "Jeszcze nic."}</p>;
  }

  if (kind === "publish") {
    const igHref = platformPostHref("ig", item);
    const fbHref = platformPostHref("fb", item);
    if (igHref || fbHref) {
      return (
        <div className="space-y-1 text-xs">
          {igHref ? (
            <a className="text-brand underline" href={igHref} target="_blank" rel="noreferrer">
              Instagram
            </a>
          ) : null}
          {fbHref ? (
            <a className="text-brand underline" href={fbHref} target="_blank" rel="noreferrer">
              Facebook
            </a>
          ) : null}
        </div>
      );
    }
    return <p className="text-xs text-muted-foreground">{running ? "Graph w toku." : "Jeszcze nic."}</p>;
  }

  return <p className="text-xs text-muted-foreground">{running ? "Czekam na wynik." : "Jeszcze nic."}</p>;
}

function RenderJobProgress({
  item,
  running,
  error,
}: {
  item?: CarouselItem;
  running: boolean;
  error?: string;
}) {
  const total = item?.render_total || 0;
  const done = item?.render_done || 0;
  const status = item?.render_status || "";
  const [nowTick, setNowTick] = useState(() => Date.now());
  useEffect(() => {
    if (!running) return;
    const id = window.setInterval(() => setNowTick(Date.now()), 500);
    return () => window.clearInterval(id);
  }, [running]);
  const live = estimateRenderProgress(
    item?.render_progress,
    { done, total, phase: status === "done" ? "done" : status || "queued" },
    nowTick,
  );
  const percent = running ? live.percent : total > 0 ? Math.round((done / total) * 100) : 0;
  return (
    <div>
      <div className="mb-1 flex items-center justify-between text-sm">
        <span>
          {done} / {total || "?"} slajdów
        </span>
        <span className="font-medium tabular-nums">{percent}%</span>
      </div>
      <div className="mb-2 h-2 overflow-hidden rounded-full bg-muted">
        <div className="h-full bg-brand transition-all" style={{ width: `${percent}%` }} />
      </div>
      <p className="text-xs text-muted-foreground">
        {running ? `ok. ${etaLabel(live.eta_sec)}` : status === "done" ? "Gotowe." : "Czekam."}
      </p>
      {error ? <p className="mt-2 text-sm text-destructive">{error}</p> : null}
      {item?.render_error && status === "failed" ? (
        <p className="mt-2 text-sm text-destructive">{item.render_error}</p>
      ) : null}
      {item?.llm_status === "design_review" || status === "design" ? (
        <LlmReceiveProgress className="mt-3" progress={item?.llm_progress} llmStatus={item?.llm_status} />
      ) : null}
    </div>
  );
}

function ReelJobProgress({
  item,
  running,
  error,
}: {
  item?: CarouselItem;
  running: boolean;
  error?: string;
}) {
  const percent = item?.reel_progress?.percent || (item?.has_reel ? 100 : running ? 8 : 0);
  const detail = item?.reel_progress?.detail || item?.reel_error || "";
  return (
    <div>
      <div className="mb-1 flex items-center justify-between text-sm">
        <span>{item?.reel_track_title || "Rolka 9:16"}</span>
        <span className="font-medium tabular-nums">{percent}%</span>
      </div>
      <div className="mb-2 h-2 overflow-hidden rounded-full bg-muted">
        <div className="h-full bg-brand transition-all" style={{ width: `${percent}%` }} />
      </div>
      <p className="text-xs text-muted-foreground">
        {running ? detail || "Składam slajdy i muzykę." : item?.has_reel ? "Gotowe." : "Czekam."}
      </p>
      {error ? <p className="mt-2 text-sm text-destructive">{error}</p> : null}
      {item?.reel_error && item.reel_status === "failed" ? (
        <p className="mt-2 text-sm text-destructive">{item.reel_error}</p>
      ) : null}
    </div>
  );
}

